import { useContext, useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Radio,
  Typography,
  Paper,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Stack,
  Alert,
  Skeleton,
} from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import { SubmitIcon } from '../../../../vectors/index';
import { useUpdateEnterpriseBillingCycle } from '@features/payments/hooks/useUpdateEnterpriseBillingCycle';
import { toast } from 'react-toastify';
import useGetEnterpriseBillingPeriod from '@features/payments/hooks/useGetEnterpriseBillingPeriod';
import { authContext } from '@staffedupusa/shared-components';
import { LoadingButton } from '@mui/lab';

const StyledPaper = styled(Paper)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  border: '2px solid transparent',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const BillingOption = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  height: '100%',
}));

const plans = [
  {
    id: 'monthly',
    title: 'Monthly',
    price: '$29',
    period: '/month',
    description: 'Perfect for getting started',
    savings: null,
  },
  {
    id: 'semi-annual',
    title: 'Semi-Annual',
    price: '$25',
    period: '/month',
    description: 'Save 14% annually',
    savings: 'Save $48/year',
  },
  {
    id: 'annual',
    title: 'Annual',
    price: '$20',
    period: '/month',
    description: 'Best value for your money',
    savings: 'Save $108/year',
  },
];

function EnterpriseBillingCycle() {
  const {authData} = useContext(authContext)
  const [selectedPlan, setSelectedPlan] = useState('');
  const [errorText, setErrorText] = useState('');
  const [pendingPlan, setPendingPlan] = useState<string | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const { updateBillingCycle, isUpdatinBillingCycle } =
    useUpdateEnterpriseBillingCycle({
      setErrorText,
    });
  const handleOpenModal = (planId: string) => {
    setPendingPlan(planId);
    setModalOpen(true);
  };

  useEffect(() => {
    if (authData?.user && authData?.user?.billing_cycle) {
      const mapPlanToId = {
        1: 'monthly',
        2: 'semi-annual',
        3: 'annual',
      } as const;
      setSelectedPlan(
        mapPlanToId[authData?.user?.billing_cycle as keyof typeof mapPlanToId],
      );
    }
  }, [authData]);


  const handleConfirmSelection = () => {
    const mapPlanToValue = {
      'monthly': 1,
      'semi-annual': 2,
      'annual': 3,
    } as const;
    const options = {
      onSuccess: (data: { status: number }) => {
        if (data.status === 200) {
          toast.success("Billing Cycle Updated Successfully")
          setPendingPlan(null);
          setModalOpen(false);
        }
      },

    }
    pendingPlan && updateBillingCycle({ billing_cycle: mapPlanToValue[pendingPlan as keyof typeof mapPlanToValue] }, options);
    // if (pendingPlan) {
    //     setSelectedPlan(pendingPlan);
    // }
    // setPendingPlan(null);
    // setModalOpen(false);
  };

  const handleCancel = () => {
    setErrorText('')
    setPendingPlan(null);
    setModalOpen(false);
  };

  return (
    <Container maxWidth="100%">
      <Typography variant="h4" component="h1" align="center" gutterBottom>
        Choose Your Billing Cycle
      </Typography>
      <Typography
        variant="subtitle1"
        align="center"
        color="text.secondary"
        sx={{ mb: 6 }}
      >
        Select the billing Cycle that works best for you
      </Typography>

      <Grid container spacing={3} alignSelf="center" textAlign="center">
        {plans.map((plan) => (
          <Grid item xs={12} md={4} key={plan.id}>
            <StyledPaper
              elevation={selectedPlan === plan.id ? 8 : 1}
              onClick={() => {
                if (selectedPlan !== plan.id) handleOpenModal(plan.id);
              }}
              sx={{
                borderColor:
                  selectedPlan === plan.id ? 'primary.main' : 'transparent',
                position: 'relative',
              }}
            >
                <BillingOption>
                  <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
                    <Radio
                      checked={selectedPlan === plan.id}
                      value={plan.id}
                      name="billing-cycle"
                      checkedIcon={<Check />}
                      sx={{ p: 0 }}
                      readOnly
                    />
                  </Box>

                  <Typography variant="h6" component="h2" gutterBottom>
                    {plan.title}
                  </Typography>
                </BillingOption>
            </StyledPaper>
          </Grid>
        ))}
      </Grid>

      {/* Confirmation Modal */}
      <Dialog open={isModalOpen} onClose={handleCancel}>
        <DialogTitle>
          <IconButton
            sx={{ position: 'absolute', top: 5, right: 5 }}
            onClick={handleCancel}
          >
            <Close />
          </IconButton>
          <br />
          <SubmitIcon />
          <br />
          <Typography variant="h4" mt={4}>
            Confirm Your Selection
          </Typography>
        </DialogTitle>
        <DialogContent>
          {errorText && <Alert severity="error">{errorText}</Alert>}
          <Typography>
            Are you sure you want to select the{' '}
            <strong>
              {plans.find((plan) => plan.id === pendingPlan)?.title}
            </strong>{' '}
            plan?
          </Typography>
          <Stack mt={2} spacing={2}>
            <Button
              startIcon={<Close />}
              variant="outlined"
              onClick={handleCancel}
              color="secondary"
              disabled={isUpdatinBillingCycle}
            >
              Cancel
            </Button>
            <LoadingButton
              loading={isUpdatinBillingCycle}
              variant="contained"
              onClick={handleConfirmSelection}
              color="primary"
              autoFocus
              startIcon={<Check />}
            >
              Confirm
            </LoadingButton>
          </Stack>
        </DialogContent>
      </Dialog>
    </Container>
  );
}

export default EnterpriseBillingCycle;
